/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { getTrackingEmotions } from '@/services/daily/complete-tasks.service';
import { getPage } from '@/services/profile/profile.service';
import { ProfilePage } from '@/graphql/generated/graphql';
import { onChangeTrackingEmotions } from '@/services/daily/emotion-wheel.service';
import { useAuthentication } from '@/hooks/useAuthentication';

export function useEmotionCalendar() {
  const [currentMonth, setCurrentMonth] = useState(dayjs().startOf('month'));
  const [page, setPage] = useState<ProfilePage | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [days, setDays] = useState<
    {
      day: number;
      emoji: string;
      label: string;
      isDisabledTracking: boolean;
      isCurrentMonth: boolean;
    }[]
  >([]);
  const { authentication, setAuthentication } = useAuthentication();

  // Compute Monday-started week indices
  const startDay = (currentMonth.startOf('month').day() + 6) % 7;
  const endDay = (currentMonth.endOf('month').day() + 6) % 7;

  const daysInMonth = currentMonth.daysInMonth();

  const handlePrevMonth = () => {
    setCurrentMonth((prev) => prev.subtract(1, 'month'));
  };

  const handleNextMonth = () => {
    setCurrentMonth((prev) => prev.add(1, 'month'));
  };

  const handleMonthData = (response: { data: any; error: string | undefined }) => {
    const previousMonth = currentMonth.clone().subtract(1, 'month');
    const nextMonth = currentMonth.clone().add(1, 'month');
    const prevMonthDaysCount = previousMonth.daysInMonth();
    const nextMonthStartDay = 1;
    const daysPerMonth: Array<any> = [];

    // Helper to find the emotion data for a given date
    const findDailyEmotion = (targetMonth: dayjs.Dayjs, dayIndex: number) => {
      return response.data.find((record: { day: string | number | dayjs.Dayjs | Date | null | undefined }) =>
        dayjs(record.day).isSame(targetMonth.startOf('month').add(dayIndex, 'day'), 'day'),
      );
    };

    // Helper to create day entry object
    const createDayEntry = ({
      dayNumber,
      targetMonth,
      dailyEmotion,
      isCurrentMonth,
    }: {
      dayNumber: number;
      targetMonth: dayjs.Dayjs;
      dailyEmotion?: any;
      isCurrentMonth: boolean;
    }) => {
      if (!dailyEmotion) {
        // No emotion data, use defaults
        return {
          day: dayNumber,
          dayComplete: `${dayNumber}/${targetMonth.format('MM')}/${targetMonth.format('YYYY')}`,
          emoji: '',
          label: '',
          isDisabledTracking: false,
          isCurrentMonth,
        };
      }

      const emotion = JSON.parse(dailyEmotion.emotion);
      return {
        day: dayNumber,
        dayComplete: `${dayNumber}/${targetMonth.format('MM')}/${targetMonth.format('YYYY')}`,
        emoji: emotion?.activeEmotionWheel?.attributes?.overallMoodIcon?.data?.attributes?.url || '',
        label: emotion?.activeEmotionWheel?.attributes?.emotion || '',
        description: emotion?.activeEmotionWheel?.attributes?.description || '',
        advice: emotion?.activeEmotionWheel?.attributes?.advice,
        isDisabledTracking: emotion?.isDisabledTracking || false,
        isCurrentMonth,
      };
    };

    // Fill in days from previous month
    // We use startDay to know how many days from the previous month to show
    for (let i = startDay - 1; i >= 0; i--) {
      const dayNumber = prevMonthDaysCount - i;
      const dailyEmotion = findDailyEmotion(previousMonth, dayNumber - 1);
      // Note: If dayNumber is 30 and month starts from index 0, we use dayNumber - 1 for zero-based indexing

      daysPerMonth.push(
        createDayEntry({
          dayNumber,
          targetMonth: previousMonth,
          dailyEmotion,
          isCurrentMonth: false,
        }),
      );
    }

    // Fill in days for the current month
    for (let i = 1; i <= daysInMonth; i++) {
      const dailyEmotion = findDailyEmotion(currentMonth, i - 1);
      daysPerMonth.push(
        createDayEntry({
          dayNumber: i,
          targetMonth: currentMonth,
          dailyEmotion,
          isCurrentMonth: true,
        }),
      );
    }

    // Fill in days from the next month
    // 6 - endDay represents how many days from the next month to fill in
    for (let i = 0; i < 6 - endDay; i++) {
      const dayNumber = nextMonthStartDay + i;
      const dailyEmotion = findDailyEmotion(nextMonth, dayNumber - 1);

      daysPerMonth.push(
        createDayEntry({
          dayNumber,
          targetMonth: nextMonth,
          dailyEmotion,
          isCurrentMonth: false,
        }),
      );
    }

    setDays(daysPerMonth);
  };

  const onChangeEmotionTrack = async () => {
    setIsLoading(true);
    await onChangeTrackingEmotions(!authentication.user.isEmotionWheelTrackingDisabled);
    setAuthentication({
      ...authentication,
      user: {
        ...authentication.user,
        isEmotionWheelTrackingDisabled: !authentication.user.isEmotionWheelTrackingDisabled,
      },
    });
    setIsLoading(false);
  };

  useEffect(() => {
    (async () => {
      const firstDay = currentMonth.clone().subtract(1, 'month').startOf('month').toDate();
      const lastDay = currentMonth.clone().add(1, 'month').endOf('month').toDate();

      // Always fetch tracking emotions when currentMonth changes
      const trackingEmotions = await getTrackingEmotions(firstDay, lastDay);
      handleMonthData(trackingEmotions);

      // Only fetch page data if it's not already set (i.e., on initial mount)
      if (!page) {
        const pageData = await getPage();
        setPage(pageData.page);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMonth, page]);

  return {
    currentMonth,
    days,
    handlePrevMonth,
    handleNextMonth,
    page,
    onChangeEmotionTrack,
    disableEmotionTracking: authentication.user.isEmotionWheelTrackingDisabled,
    isLoading,
  };
}
